package co.maytes.standupbot.dashboard.features.bootstrap

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.integrations.PanelAction
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.appcredentials.AnthropicAppCredentialsDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialPreviewDto
import co.maytes.standupbot.shared.dto.appcredentials.AppCredentialProviderDto
import co.maytes.standupbot.shared.dto.appcredentials.TestConnectionResultDto
import co.maytes.standupbot.shared.dto.bootstrap.BootstrapStateDto

/**
 * Sprint 8H — dashboard-side store for the `/api/v1/bootstrap` endpoints.
 * Mirrors [co.maytes.standupbot.dashboard.features.integrations.AppCredentialsStore]
 * but without a workspaceId (bootstrap is pre-workspace).
 */
class BootstrapStore(private val api: ApiClient) {
    var loadState: LoadState by mutableStateOf(LoadState.Idle)
        private set

    var finalized: Boolean by mutableStateOf(false)
        private set

    private val previewByProvider = mutableStateMapOf<AppCredentialProviderDto, AppCredentialPreviewDto>()
    private val panelActionByProvider = mutableStateMapOf<AppCredentialProviderDto, PanelAction>()
    private val testResultByProvider = mutableStateMapOf<AppCredentialProviderDto, TestConnectionResultDto>()

    fun preview(provider: AppCredentialProviderDto): AppCredentialPreviewDto? = previewByProvider[provider]

    fun panelAction(provider: AppCredentialProviderDto): PanelAction =
        panelActionByProvider[provider] ?: PanelAction.Idle

    fun lastTestResult(provider: AppCredentialProviderDto): TestConnectionResultDto? = testResultByProvider[provider]

    val allProvidersConfigured: Boolean
        get() = AppCredentialProviderDto.entries.all { previewByProvider[it]?.present == true }

    suspend fun refresh() {
        loadState = LoadState.Loading
        when (val res = api.get<BootstrapStateDto>("/api/v1/bootstrap/state")) {
            is Result.Ok -> {
                finalized = res.value.finalized
                previewByProvider.clear()
                res.value.providers.forEach { entry ->
                    entry.preview?.let { previewByProvider[entry.provider] = it }
                }
                loadState = LoadState.Ready
            }
            is Result.Err -> loadState = LoadState.Error(res.error)
        }
    }

    suspend fun saveAnthropic(body: AnthropicAppCredentialsDto): Result<AppCredentialPreviewDto, ApiError> =
        saveInternal(AppCredentialProviderDto.ANTHROPIC, body)

    private suspend inline fun <reified TBody : Any> saveInternal(
        provider: AppCredentialProviderDto,
        body: TBody,
    ): Result<AppCredentialPreviewDto, ApiError> {
        panelActionByProvider[provider] = PanelAction.Saving
        val result =
            api.put<TBody, AppCredentialPreviewDto>(
                path = "/api/v1/bootstrap/credentials/${provider.name.lowercase()}",
                body = body,
            )
        panelActionByProvider[provider] = PanelAction.Idle
        if (result is Result.Ok) previewByProvider[provider] = result.value
        return result
    }

    suspend fun testConnection(provider: AppCredentialProviderDto): Result<TestConnectionResultDto, ApiError> {
        panelActionByProvider[provider] = PanelAction.Testing
        val result =
            api.post<TestConnectionResultDto>(
                path = "/api/v1/bootstrap/credentials/${provider.name.lowercase()}/test",
            )
        panelActionByProvider[provider] = PanelAction.Idle
        if (result is Result.Ok) testResultByProvider[provider] = result.value
        return result
    }
}

@Composable
fun rememberBootstrapStore(): BootstrapStore {
    val api = rememberApiClient()
    return remember(api) { BootstrapStore(api) }
}
